import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Gift, Sparkles, ChevronRight, Store, Wallet, Send, Loader2, CheckCircle2, RefreshCcw,
  Heart, Cake, PartyPopper, Star, ShieldCheck, Clock, Zap, Mail, ShoppingBag, ArrowRight, QrCode } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/components/VIP";

const fmtM = (v) => `$${(Number(v || 0)).toFixed(2)}`;
const fmtD = (iso) => (iso ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "");

/* Rich, festive gift-card visual with holographic sheen, theme swaps + optional recipient name.
   `design` is an object: { key, label, corner_label, image_url?, gradient?, pattern?, pattern_size?, pattern_opacity? }.
   If image_url is present it wins as the card background. */
const FALLBACK_DESIGN = {
  key: "classic", label: "Classic", corner_label: "Gift",
  gradient: "linear-gradient(135deg,#df1721 0%,#df1721 40%,#7c0d11 75%,#1a0202 100%)",
  pattern: "radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)",
  pattern_size: "18px 18px", pattern_opacity: 0.08,
};

function CardMock({ amount, code, scope, sellerName, recipient, design, floating = false, small = false }) {
  const t = design || FALLBACK_DESIGN;
  const bgStyle = t.image_url
    ? { backgroundImage: `url(${fileUrl(t.image_url)})`, backgroundSize: "cover", backgroundPosition: "center" }
    : { background: t.gradient || FALLBACK_DESIGN.gradient };
  return (
    <div className={`gc-card group relative ${floating ? "gc-float" : ""} ${small ? "max-w-[280px]" : ""}`} data-testid="gift-card-mock" data-theme={t.key}>
      <div className="gc-card-inner relative rounded-[26px] overflow-hidden text-white shadow-2xl" style={bgStyle}>
        {/* Dark overlay for readability when a photo is used */}
        {t.image_url && <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(0,0,0,0.35), rgba(0,0,0,0.6))" }} />}
        {/* Holographic sheen */}
        <div className="gc-sheen" />
        {/* Optional pattern overlay */}
        {t.pattern && (
          <div className="absolute inset-0" style={{
            backgroundImage: t.pattern, backgroundSize: t.pattern_size || "auto",
            opacity: (t.pattern_opacity ?? 0.35),
          }} />
        )}
        {/* Corner ribbon */}
        <div className="absolute top-0 right-0 text-white font-black text-[10px] uppercase tracking-widest px-3 pt-2 pb-1.5 rounded-bl-2xl shadow-lg z-10 leading-none backdrop-blur-sm" style={{ backgroundColor: "rgba(0,0,0,0.68)" }}>
          <Gift size={11} className="inline -mt-0.5 mr-1 align-middle" /> {t.corner_label || "Gift"}
        </div>
        <div className={`relative ${small ? "p-5 pb-4" : "p-7 pb-6"}`}>
          <p className="text-[10px] font-extrabold uppercase tracking-[0.18em] opacity-90">
            Gulf Coast VIP {scope === "universal" ? "· Universal" : (sellerName ? `· ${sellerName}` : "")}
          </p>
          <p className={`font-display font-black mt-3 leading-none ${small ? "text-3xl" : "text-5xl"}`}>{fmtM(amount)}</p>
          {recipient ? <p className="text-sm mt-3 italic opacity-95">To {recipient}</p> : null}
          <div className="mt-6 opacity-95">
            <p className="text-[10px] uppercase tracking-widest opacity-80">Redemption code</p>
            <p className={`font-mono tracking-widest font-bold mt-0.5 ${small ? "text-sm" : "text-lg"}`}>{code || "GCV-XXXX-XXXX-XXXX"}</p>
          </div>
          <div className="mt-5 flex items-end justify-between gap-4">
            <p className="text-[10px] uppercase tracking-[0.22em] opacity-80">One card. The entire coast.</p>
            {/* Holographic brand mark — iridescent foil masked by the logo shape */}
            <div className={`gc-brand-holo relative shrink-0 ${small ? "h-11 w-11" : "h-14 w-14"}`} aria-hidden="true">
              <div className="gc-brand-holo__foil" />
              <div className="gc-brand-holo__glare" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* Small floating gift dots as ambient festive background. */
function Confetti() {
  const dots = useMemo(() => Array.from({ length: 20 }, (_, i) => ({
    left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%`,
    size: 4 + Math.random() * 8, delay: (Math.random() * 6).toFixed(2),
    duration: (5 + Math.random() * 6).toFixed(2), color: ["#df1721", "#df1721", "#ffffff", "#ffffff"][i % 4],
  })), []);
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
      {dots.map((d, i) => (
        <span key={i} className="gc-confetti" style={{
          left: d.left, top: d.top, width: d.size, height: d.size,
          background: d.color, animationDelay: `${d.delay}s`,
          animationDuration: `${d.duration}s`,
        }} />
      ))}
    </div>
  );
}

/* Local styles (float + sheen + confetti keyframes) injected once. */
function GiftCardStyles() {
  return (
    <style>{`
      .gc-float { animation: gcFloat 6s ease-in-out infinite; }
      @keyframes gcFloat { 0%,100% { transform: translateY(0) rotate(-1.5deg);} 50% { transform: translateY(-10px) rotate(-3deg);} }
      .gc-card-inner { transform: rotate(-2deg); transition: transform 400ms ease; }
      .gc-card:hover .gc-card-inner { transform: rotate(0) scale(1.02); }
      .gc-sheen { position: absolute; inset: -50%; background: linear-gradient(115deg, transparent 40%, rgba(255,255,255,0.28) 50%, transparent 60%); transform: translateX(-40%); animation: gcSheen 5s ease-in-out infinite; }
      @keyframes gcSheen { 0%,100% { transform: translateX(-40%);} 50% { transform: translateX(40%);} }
      .gc-confetti { position: absolute; border-radius: 9999px; opacity: 0.6; animation: gcRise linear infinite; }
      @keyframes gcRise { 0% { transform: translateY(20px) scale(0.6); opacity: 0;} 20% { opacity: 0.7;} 100% { transform: translateY(-140px) scale(1); opacity: 0;} }
      .gc-shimmer { background: linear-gradient(90deg, transparent, rgba(255,255,255,0.12), transparent); background-size: 200% 100%; animation: gcShimmer 3s infinite linear; }
      @keyframes gcShimmer { from { background-position: 200% 0;} to { background-position: -200% 0;} }
      /* Holographic brand mark — iridescent foil visible only through the logo silhouette (CSS mask). */
      .gc-brand-holo { position: relative; filter: drop-shadow(0 2px 4px rgba(0,0,0,0.5)); }
      .gc-brand-holo__foil, .gc-brand-holo__glare {
        -webkit-mask-image: url('/gc-brand-mask.png'); mask-image: url('/gc-brand-mask.png');
        -webkit-mask-size: contain; mask-size: contain;
        -webkit-mask-repeat: no-repeat; mask-repeat: no-repeat;
        -webkit-mask-position: center; mask-position: center;
      }
      .gc-brand-holo__foil {
        position: absolute; inset: 0; z-index: 10;
        background: conic-gradient(from 210deg,
          #ffffff 0%, #ffd6e6 12%, #fff0bf 24%, #c4e9ff 38%, #e3d4ff 52%,
          #ffffff 64%, #c6ffdf 76%, #fff0bf 88%, #ffffff 100%);
        animation: gcHolo 5.5s linear infinite;
      }
      .gc-brand-holo__glare {
        position: absolute; inset: 0; z-index: 20; pointer-events: none;
        background: linear-gradient(115deg, transparent 30%, rgba(255,255,255,0.9) 50%, transparent 70%);
        background-size: 250% 100%;
        animation: gcGlareSweep 4.5s ease-in-out infinite;
      }
      @keyframes gcHolo {
        0%   { filter: hue-rotate(0deg)   saturate(1.35); }
        50%  { filter: hue-rotate(180deg) saturate(1.6); }
        100% { filter: hue-rotate(360deg) saturate(1.35); }
      }
      @keyframes gcGlareSweep { 0%,100% { background-position: 200% 0; } 50% { background-position: -50% 0; } }
    `}</style>
  );
}

/* ================================ /gift-cards ================================ */
export default function GiftCards() {
  useSeo("Gulf Coast VIP Gift Cards | Give the Whole Coast in One Card",
    "Buy universal or merchant gift cards, check balances, reload cards, and gift experiences across venues, artists, tickets, activities and merch. Branded email delivery + scheduled sending.");
  const nav = useNavigate(); // eslint-disable-line no-unused-vars
  const { user } = useAuth();
  const [settings, setSettings] = useState({ denominations: [25, 50, 100, 250], allow_reload: true, min_amount: 5, max_amount: 1000 });
  const [designs, setDesigns] = useState([FALLBACK_DESIGN]);
  const [designKey, setDesignKey] = useState("classic");
  const [amount, setAmount] = useState(100);
  const [customAmount, setCustomAmount] = useState("");
  const [recipient, setRecipient] = useState("");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [buyerEmail, setBuyerEmail] = useState("");
  const [message, setMessage] = useState("");
  const [deliveryDate, setDeliveryDate] = useState("");
  const [sellers, setSellers] = useState([]);
  const [sellerId, setSellerId] = useState("");
  const [buying, setBuying] = useState(false);
  const [tab, setTab] = useState("universal"); // universal | merchant

  useEffect(() => {
    api.get("/gift-cards/settings").then((r) => { setSettings(r.data); setAmount(r.data.denominations?.[2] || 100); }).catch(() => {});
    api.get("/gift-cards/designs").then((r) => {
      const all = [...(r.data.custom || []), ...(r.data.defaults || [])];
      setDesigns(all.length ? all : [FALLBACK_DESIGN]);
      setDesignKey(all[0]?.key || "classic");
    }).catch(() => {});
    api.get("/gift-cards/sellers").then((r) => setSellers(r.data || [])).catch(() => {});
  }, []);

  const effectiveAmount = customAmount ? Math.max(5, Math.min(1000, Number(customAmount) || 0)) : amount;
  const activeDesign = useMemo(() => designs.find((d) => d.key === designKey) || designs[0] || FALLBACK_DESIGN, [designs, designKey]);
  const selectedSeller = sellers.find((s) => s.id === sellerId);

  const gotoShop = (scope) => {
    if (scope === "merchant" || scope === "universal") setTab(scope);
    document.getElementById("gc-designer")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const buyNow = async () => {
    if (!recipientEmail.trim() || !recipientEmail.includes("@")) { toast.error("Enter the recipient's email — the card is delivered there"); return; }
    if (!user && (!buyerEmail.trim() || !buyerEmail.includes("@"))) { toast.error("Enter your email for the receipt"); return; }
    if (tab === "merchant" && !sellerId) { toast.error("Pick a merchant for a merchant gift card"); return; }
    setBuying(true);
    try {
      const { data } = await api.post("/gift-cards/purchase/checkout", {
        scope: tab, seller_id: tab === "merchant" ? sellerId : "",
        amount: effectiveAmount, theme: designKey,
        recipient_email: recipientEmail.trim(), recipient_name: recipient.trim(),
        message: message.trim(), delivery_at: deliveryDate || "",
        buyer_email: buyerEmail.trim(), origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) { toast.error(errMsg(e)); setBuying(false); }
  };

  return (
    <div data-testid="gift-cards-landing" className="text-white">
      <GiftCardStyles />

      {/* ============ HERO ============ */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0" style={{ background: "radial-gradient(1200px 500px at 12% 10%, rgba(223,23,33,0.28), transparent 55%), radial-gradient(1000px 600px at 90% 85%, rgba(223,23,33,0.20), transparent 55%), #050505" }} />
        <Confetti />
        <div className="relative max-w-6xl mx-auto px-6 md:px-10 py-20 md:py-28 grid lg:grid-cols-[1.05fr_.95fr] gap-14 items-center">
          <div>
            <p className="inline-flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-widest bg-white/10 border border-white/15 backdrop-blur px-3 py-1.5 rounded-full mb-6">
              <PartyPopper size={13} className="text-[#df1721]" /> The Gulf Coast VIP Gift Card
            </p>
            <h1 className="font-display font-black text-5xl sm:text-6xl md:text-7xl leading-[0.9]">
              Give the whole<br /><span className="italic" style={{ background: "linear-gradient(90deg,#df1721,#ffffff)", WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" }}>Gulf Coast</span><br />
              <span className="text-white">in one card.</span>
            </h1>
            <p className="text-lg text-zinc-300 mt-6 leading-relaxed max-w-xl">
              A single card that unlocks live music, tickets, dinners, watersports, spotlights, merch and more. Universal cards spend anywhere in the marketplace. Merchant cards fund a favorite spot. Branded email delivery, scheduled sending, and lifetime reloads — done in seconds.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <button onClick={() => gotoShop()} className="bg-[#df1721] hover:bg-[#c0141d] text-white font-bold px-6 py-3.5 rounded-full flex items-center gap-2 shadow-[0_10px_40px_-8px_rgba(223,23,33,0.6)]" data-testid="gc-shop-cta">
                <Sparkles size={17} /> Buy a gift card <ArrowRight size={15} />
              </button>
              <Link to="/gift-cards/check" className="border border-white/20 hover:border-[#df1721] hover:text-white text-white font-medium px-6 py-3.5 rounded-full flex items-center gap-2" data-testid="gc-check-cta">
                <Wallet size={15} /> Check balance / Reload
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-5 text-xs text-zinc-400">
              <span className="flex items-center gap-1.5"><ShieldCheck size={13} className="text-[#df1721]" /> Secure code + PIN</span>
              <span className="flex items-center gap-1.5"><Clock size={13} className="text-[#df1721]" /> No expiry</span>
              <span className="flex items-center gap-1.5"><Zap size={13} className="text-[#df1721]" /> Instant delivery</span>
              <span className="flex items-center gap-1.5"><RefreshCcw size={13} className="text-[#df1721]" /> Reloadable</span>
            </div>
          </div>
          <div className="relative">
            {/* Radial glow behind card */}
            <div className="absolute inset-0 -z-0" style={{ background: "radial-gradient(closest-side, rgba(223,23,33,0.45), transparent 70%)", filter: "blur(30px)" }} />
            <div className="relative mx-auto w-full max-w-md">
              <CardMock amount={effectiveAmount} code="GCV-XXXX-XXXX-XXXX" scope={tab}
                sellerName={tab === "merchant" ? "The Lantern Pier" : ""}
                recipient={recipient || "someone amazing"} design={activeDesign} floating />
            </div>
          </div>
        </div>
      </section>

      {/* ============ INTERACTIVE DESIGN + SHOP WIDGET ============ */}
      <section id="gc-designer" className="max-w-6xl mx-auto px-6 md:px-10 py-16 scroll-mt-24">
        <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 md:p-10 grid lg:grid-cols-2 gap-10 items-center relative overflow-hidden" data-testid="gc-designer">
          <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full opacity-30" style={{ background: "radial-gradient(circle, rgba(223,23,33,0.5), transparent 70%)" }} />
          <div>
            <p className="overline mb-2">Design your gift</p>
            <h2 className="font-display font-black text-3xl md:text-4xl mb-3">Build & buy in 20 seconds.</h2>
            <p className="text-zinc-400 mb-6 text-sm leading-relaxed">Pick a scope, dial in the amount, personalize it, hit Buy. What you preview is what your recipient opens in their inbox.</p>

            {/* Scope tabs */}
            <div className="grid grid-cols-2 gap-2 mb-4">
              <button onClick={() => setTab("universal")} data-testid="gc-tab-universal"
                className={`rounded-xl border px-4 py-3 text-sm text-left flex items-center gap-3 transition-colors ${tab === "universal" ? "border-[#df1721] bg-[#df1721]/12 text-white" : "border-white/10 hover:border-white/30 text-zinc-300"}`}>
                <Sparkles size={16} className="text-[#df1721]" />
                <div>
                  <p className="font-bold">Universal</p>
                  <p className="text-[11px] text-zinc-500">Any seller in the marketplace</p>
                </div>
              </button>
              <button onClick={() => setTab("merchant")} data-testid="gc-tab-merchant"
                className={`rounded-xl border px-4 py-3 text-sm text-left flex items-center gap-3 transition-colors ${tab === "merchant" ? "border-[#df1721] bg-[#df1721]/12 text-white" : "border-white/10 hover:border-white/30 text-zinc-300"}`}>
                <Store size={16} className="text-[#df1721]" />
                <div>
                  <p className="font-bold">Merchant</p>
                  <p className="text-[11px] text-zinc-500">Locked to one seller</p>
                </div>
              </button>
            </div>

            {tab === "merchant" && (
              <div className="mb-4">
                <p className="text-[11px] uppercase tracking-widest text-zinc-500 mb-2">Merchant</p>
                <select value={sellerId} onChange={(e) => setSellerId(e.target.value)} data-testid="gc-seller-picker"
                  className="w-full bg-black/40 border border-white/10 rounded-lg px-4 py-3 text-sm outline-none focus:border-[#df1721]">
                  <option value="">Pick a merchant…</option>
                  {sellers.map((s) => <option key={s.id} value={s.id}>{s.display_name}</option>)}
                </select>
                {sellers.length === 0 && <p className="text-[11px] text-zinc-500 mt-1.5">No active merchants yet — send a Universal card instead.</p>}
              </div>
            )}

            {/* Denominations */}
            <div className="mb-3">
              <p className="text-[11px] uppercase tracking-widest text-zinc-500 mb-2">Amount</p>
              <div className="flex flex-wrap gap-2">
                {(settings.denominations || [25, 50, 100, 250]).map((d) => (
                  <button key={d} onClick={() => { setAmount(d); setCustomAmount(""); }} data-testid={`gc-denom-${d}`}
                    className={`px-4 py-2 rounded-full text-sm font-bold border transition-colors ${amount === d && !customAmount ? "bg-[#df1721] border-[#df1721] text-white" : "border-white/15 hover:border-white/35 text-zinc-200"}`}>
                    ${d}
                  </button>
                ))}
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 text-sm">$</span>
                  <input type="number" min={5} max={1000} placeholder="Custom" value={customAmount}
                    onChange={(e) => setCustomAmount(e.target.value)} data-testid="gc-denom-custom"
                    className={`pl-6 pr-3 py-2 rounded-full text-sm w-28 font-bold bg-transparent border transition-colors ${customAmount ? "border-[#df1721] text-white" : "border-white/15 text-zinc-200"} focus:outline-none focus:border-[#df1721]`} />
                </div>
              </div>
            </div>

            {/* Recipient + delivery */}
            <div className="mb-6 space-y-3">
              <p className="text-[11px] uppercase tracking-widest text-zinc-500">Deliver to</p>
              <div className="grid sm:grid-cols-2 gap-3">
                <input value={recipientEmail} onChange={(e) => setRecipientEmail(e.target.value)} data-testid="gc-recipient-email"
                  type="email" placeholder="Recipient's email (required)"
                  className="w-full bg-black/40 border border-white/10 rounded-lg px-4 py-3 text-sm outline-none focus:border-[#df1721]" />
                <input value={recipient} onChange={(e) => setRecipient(e.target.value)} data-testid="gc-recipient-preview"
                  placeholder="Recipient's first name" maxLength={40}
                  className="w-full bg-black/40 border border-white/10 rounded-lg px-4 py-3 text-sm outline-none focus:border-[#df1721]" />
              </div>
              <textarea value={message} onChange={(e) => setMessage(e.target.value)} data-testid="gc-message"
                rows={2} maxLength={500} placeholder="Personal message (optional) — shows inside the email"
                className="w-full bg-black/40 border border-white/10 rounded-lg px-4 py-3 text-sm outline-none focus:border-[#df1721] resize-none" />
              <div className="grid sm:grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-[11px] text-zinc-500">Delivery date (optional — blank = instant)</span>
                  <input type="date" value={deliveryDate} min={new Date().toISOString().slice(0, 10)}
                    onChange={(e) => setDeliveryDate(e.target.value)} data-testid="gc-delivery-date"
                    className="mt-1 w-full bg-black/40 border border-white/10 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-[#df1721]" />
                </label>
                {!user && (
                  <label className="block">
                    <span className="text-[11px] text-zinc-500">Your email (for the receipt)</span>
                    <input type="email" value={buyerEmail} onChange={(e) => setBuyerEmail(e.target.value)} data-testid="gc-buyer-email"
                      placeholder="you@example.com"
                      className="mt-1 w-full bg-black/40 border border-white/10 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-[#df1721]" />
                  </label>
                )}
              </div>
            </div>

            {/* Theme picker */}
            <div className="mb-6">
              <p className="text-[11px] uppercase tracking-widest text-zinc-500 mb-2">Card design</p>
              <div className="grid grid-cols-4 gap-2" data-testid="gc-theme-picker">
                {designs.map((info) => {
                  const active = designKey === info.key;
                  const swatchBg = info.image_url
                    ? { backgroundImage: `url(${fileUrl(info.image_url)})`, backgroundSize: "cover", backgroundPosition: "center" }
                    : { background: info.gradient || FALLBACK_DESIGN.gradient };
                  return (
                    <button key={info.key} onClick={() => setDesignKey(info.key)} data-testid={`gc-theme-${info.key}`} title={info.label}
                      className={`relative rounded-xl overflow-hidden aspect-[3/2] border transition-all ${active ? "border-[#df1721] ring-2 ring-[#df1721]/60 scale-[1.02]" : "border-white/10 hover:border-white/40"}`}>
                      <div className="absolute inset-0" style={swatchBg} />
                      {!info.image_url && info.pattern && (
                        <div className="absolute inset-0" style={{
                          backgroundImage: info.pattern, backgroundSize: info.pattern_size || "auto",
                          opacity: info.pattern_opacity ?? 0.35,
                        }} />
                      )}
                      {info.image_url && <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0), rgba(0,0,0,0.7))" }} />}
                      <div className="absolute inset-0 flex items-end p-2">
                        <span className="text-[10px] font-extrabold uppercase tracking-widest text-white drop-shadow">{info.label}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <button onClick={buyNow} disabled={buying} className="w-full bg-[#df1721] hover:bg-[#c0141d] text-white font-bold py-3.5 rounded-full flex items-center justify-center gap-2 shadow-[0_10px_40px_-8px_rgba(223,23,33,0.6)] disabled:opacity-50" data-testid="gc-designer-buy">
              {buying ? <Loader2 size={17} className="animate-spin" /> : <ShoppingBag size={17} />} Buy now — {fmtM(effectiveAmount)} <ArrowRight size={15} />
            </button>
            <p className="text-[11px] text-zinc-500 mt-2 text-center flex items-center justify-center gap-1.5"><ShieldCheck size={12} className="text-[#df1721]" /> Secure Stripe checkout — card emailed with code, PIN & QR after payment.</p>
          </div>

          <div className="relative">
            <div className="absolute inset-0 -z-0" style={{ background: "radial-gradient(closest-side, rgba(223,23,33,0.35), transparent 70%)", filter: "blur(30px)" }} />
            <div className="relative mx-auto w-full max-w-sm">
              <CardMock amount={effectiveAmount} code="GCV-XXXX-XXXX-XXXX" scope={tab}
                sellerName={tab === "merchant" ? (selectedSeller?.display_name || "Any seller you choose") : ""}
                recipient={recipient || "your favorite person"} design={activeDesign} />
            </div>
            <div className="mt-4 mx-auto max-w-sm rounded-xl border border-white/10 bg-black/40 p-3 flex items-center gap-3 text-xs text-zinc-400">
              <Mail size={14} className="text-[#df1721] shrink-0" /> Delivered as a branded email with card, PIN, QR code and a "reload / check balance" button.
            </div>
          </div>
        </div>
      </section>

      {/* ============ PERFECT FOR (OCCASIONS) ============ */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 py-14">
        <p className="overline mb-3">Perfect for</p>
        <h2 className="font-display font-black text-3xl md:text-4xl mb-8">A gift for every reason.</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="gc-occasions">
          {[
            [Cake, "Birthdays"], [Heart, "Anniversaries"], [PartyPopper, "Just Because"],
            [Sparkles, "Thank You"], [Gift, "Holidays"], [Star, "Milestones"],
          ].map(([Icon, label]) => (
            <div key={label} className="group rounded-2xl border border-white/10 bg-white/[0.02] p-5 text-center hover:border-[#df1721]/60 hover:bg-[#df1721]/[0.08] transition-colors cursor-default">
              <Icon size={22} className="mx-auto text-[#df1721] group-hover:scale-110 transition-transform" />
              <p className="font-display font-bold mt-2 text-sm">{label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ============ UNIVERSAL vs MERCHANT ============ */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 py-16 border-t border-white/5">
        <p className="overline mb-3">Two flavors</p>
        <h2 className="font-display font-black text-3xl md:text-4xl mb-10">Universal & Merchant cards.</h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="relative rounded-2xl border border-[#df1721]/50 bg-gradient-to-br from-[#df1721]/15 via-[#df1721]/[0.06] to-transparent p-8 overflow-hidden" data-testid="gc-universal">
            <div className="absolute -top-8 -right-8 w-48 h-48 rounded-full opacity-30" style={{ background: "radial-gradient(circle, rgba(223,23,33,0.6), transparent 70%)" }} />
            <div className="relative">
              <div className="flex items-center gap-2 text-[#df1721]"><Sparkles size={18} /><p className="overline !text-[#df1721]">Most gifted</p></div>
              <h3 className="font-display font-bold text-2xl mt-3">Gulf Coast VIP Universal</h3>
              <p className="text-zinc-300 mt-3 leading-relaxed">One card, redeemable at every participating seller in the marketplace. Perfect when you know they love the coast but not their exact vibe.</p>
              <ul className="text-sm text-zinc-300 mt-5 space-y-2">
                {["Spend anywhere in the marketplace", "No expiry, no hidden fees", "Reloadable any time", "Instant or scheduled delivery"].map((t) => (
                  <li key={t} className="flex items-center gap-2"><CheckCircle2 size={14} className="text-[#df1721] shrink-0" /> {t}</li>
                ))}
              </ul>
              <button onClick={() => gotoShop("universal")} className="mt-6 inline-flex items-center gap-2 bg-[#df1721] hover:bg-[#c0141d] text-white font-bold px-5 py-2.5 rounded-full text-sm">
                <Sparkles size={14} /> Send a universal card
              </button>
            </div>
          </div>
          <div className="relative rounded-2xl border border-white/10 bg-white/[0.03] p-8 overflow-hidden" data-testid="gc-merchant">
            <div className="flex items-center gap-2 text-zinc-300"><Store size={18} /><p className="overline">Specific vibes</p></div>
            <h3 className="font-display font-bold text-2xl mt-3">Merchant Cards</h3>
            <p className="text-zinc-300 mt-3 leading-relaxed">Issued by an individual venue, artist, attraction or business — redeemable only in that shop. Perfect for gifting a very specific experience.</p>
            <ul className="text-sm text-zinc-300 mt-5 space-y-2">
              {["Locked to the issuing merchant", "Same branded email + QR + reload", "Ideal for gifting a specific tab or event", "Great repeat-guest bonus"].map((t) => (
                <li key={t} className="flex items-center gap-2"><CheckCircle2 size={14} className="text-zinc-400 shrink-0" /> {t}</li>
              ))}
            </ul>
            <button onClick={() => gotoShop("merchant")} className="mt-6 inline-flex items-center gap-2 border border-white/15 hover:border-[#df1721] hover:text-white text-white font-bold px-5 py-2.5 rounded-full text-sm">
              <Store size={14} /> Send a merchant card
            </button>
          </div>
        </div>
      </section>

      {/* ============ HOW IT WORKS ============ */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 py-16 border-t border-white/5">
        <p className="overline mb-3">Simple, four steps</p>
        <h2 className="font-display font-black text-3xl md:text-4xl mb-10">How it works.</h2>
        <div className="grid md:grid-cols-4 gap-5">
          {[
            [Gift, "Pick a card", "Choose Universal or a specific merchant right here on this page. Set an amount from $5 to $1,000."],
            [Send, "Deliver it", "Send it right away or pick a future date. Your recipient gets a branded email with the card design, code + PIN, and a QR."],
            [Wallet, "Redeem at checkout", "One field for merchant + universal cards. The balance covers up to the order total; the rest is paid by card."],
            [RefreshCcw, "Reload for life", "Balances never expire. Top up any card by scanning the QR or entering the code on the balance page."],
          ].map(([Icon, t, b], i) => (
            <div key={t} className="relative rounded-2xl border border-white/10 p-5 bg-white/[0.02] overflow-hidden group hover:border-[#df1721]/50 transition-colors">
              <div className="absolute top-3 right-3 font-display font-black text-4xl text-white/[0.06]">{i + 1}</div>
              <Icon size={22} className="text-[#df1721] mb-3" />
              <p className="font-display font-bold">{t}</p>
              <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{b}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ============ QUICK BALANCE CHECK STRIP ============ */}
      <QuickCheckStrip />

      {/* ============ WHY GULF COAST VIP ============ */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 py-16 border-t border-white/5">
        <p className="overline mb-3">Three big reasons</p>
        <h2 className="font-display font-black text-3xl md:text-4xl mb-10">Why gift a Gulf Coast VIP card.</h2>
        <div className="grid md:grid-cols-3 gap-5">
          {[
            [QrCode, "Redeem anywhere", "Same code + PIN field on every seller's checkout — no fumbling for a merchant-specific URL."],
            [Mail, "Actually beautiful", "Branded email with a designed card, personalized name, message, QR code and reload link."],
            [ShieldCheck, "Safe by default", "PIN-protected, cancellable, adjustable and fully audited via a permanent transaction ledger."],
          ].map(([Icon, t, b]) => (
            <div key={t} className="rounded-2xl border border-white/10 p-6 bg-white/[0.02] hover:border-[#df1721]/50 hover:bg-[#df1721]/[0.06] transition-colors">
              <Icon size={22} className="text-[#df1721] mb-3" />
              <p className="font-display font-bold text-lg">{t}</p>
              <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{b}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ============ FAQ ============ */}
      <section className="max-w-4xl mx-auto px-6 md:px-10 py-16 border-t border-white/5">
        <p className="overline mb-3">Good questions</p>
        <h2 className="font-display font-black text-3xl md:text-4xl mb-8">FAQ.</h2>
        <div className="space-y-4" data-testid="gc-faq">
          {[
            ["Do gift cards expire?", "Never. Balances stick around until they're spent."],
            ["Can I add funds to a card someone gave me?", "Yes — head to /gift-cards/check, enter the code + PIN, and use the Reload button. Any amount from $5 to $1,000."],
            ["What happens if my order costs more than my card balance?", "The card covers what it can and the remainder is charged to your card at checkout. No manual math."],
            ["Can I use a merchant card at a different seller?", "Merchant cards are locked to the seller who issued them. Buy a Universal card if you want anywhere-redeemable."],
            ["Can I schedule a future delivery date?", "Yes. During checkout you'll set the delivery date — your recipient's email lands on that day."],
            ["Are the balance and PIN safe?", "PINs are stored as a one-way hash — even we can't read them. Cards can be cancelled, adjusted or refunded any time by an admin."],
          ].map(([q, a]) => (
            <details key={q} className="group rounded-xl border border-white/10 bg-white/[0.02] px-5 py-4 cursor-pointer">
              <summary className="flex items-center justify-between gap-3 list-none">
                <span className="font-display font-bold text-white">{q}</span>
                <ChevronRight size={16} className="text-[#df1721] transition-transform group-open:rotate-90" />
              </summary>
              <p className="text-sm text-zinc-400 mt-3 leading-relaxed">{a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="max-w-4xl mx-auto px-6 md:px-10 py-20 text-center">
        <div className="relative rounded-3xl border border-[#df1721]/50 overflow-hidden p-10 md:p-14"
          style={{ background: "linear-gradient(135deg,rgba(223,23,33,0.18),rgba(223,23,33,0.06) 40%,transparent 70%)" }}>
          <Confetti />
          <div className="relative">
            <Gift size={34} className="mx-auto text-[#df1721] mb-4" />
            <h2 className="font-display font-black text-3xl md:text-5xl leading-tight">Ready to gift the Gulf Coast?</h2>
            <p className="text-zinc-300 mt-4 max-w-xl mx-auto">Pick a denomination, personalize a note, deliver it now — or on the exact day it should land.</p>
            <button onClick={() => gotoShop()}
              className="mt-8 inline-flex items-center gap-2 bg-[#df1721] hover:bg-[#c0141d] text-white font-bold px-7 py-3.5 rounded-full shadow-[0_10px_40px_-8px_rgba(223,23,33,0.6)]" data-testid="gc-final-cta">
              <Sparkles size={17} /> Buy a gift card <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}

/* ---- Quick balance check strip (inline widget on the landing page) ---- */
function QuickCheckStrip() {
  const nav = useNavigate();
  const [code, setCode] = useState("");
  return (
    <section className="max-w-6xl mx-auto px-6 md:px-10 py-14">
      <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 md:p-8 flex flex-col md:flex-row items-center gap-5" data-testid="gc-quick-check">
        <div className="flex-1">
          <p className="overline mb-2">Already have a card?</p>
          <h3 className="font-display font-bold text-2xl">Check the balance in one field.</h3>
          <p className="text-sm text-zinc-400 mt-2">Punch in the code — the next screen will ask for the PIN and show your full transaction history + reload options.</p>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); nav(`/gift-cards/check?code=${encodeURIComponent(code.toUpperCase())}`); }}
          className="flex flex-col sm:flex-row gap-2 w-full md:w-auto md:min-w-[420px]">
          <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="GCV-XXXX-XXXX-XXXX" data-testid="gc-quick-code"
            className="flex-1 bg-black/40 border border-white/15 rounded-full px-5 py-3 text-sm font-mono tracking-widest outline-none focus:border-[#df1721]" />
          <button className="bg-[#df1721] hover:bg-[#c0141d] text-white font-bold px-6 py-3 rounded-full flex items-center justify-center gap-2" data-testid="gc-quick-submit">
            <Wallet size={15} /> Check balance
          </button>
        </form>
      </div>
    </section>
  );
}

/* ============================ /gift-cards/check ============================ */
export function GiftCardCheck() {
  useSeo("Check gift card balance | Gulf Coast VIP",
    "Look up the current balance and transaction history for any Gulf Coast VIP gift card. Reload with one click.");
  const [params] = useSearchParams();
  const [code, setCode] = useState((params.get("code") || "").toUpperCase());
  const [pin, setPin] = useState("");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reloadAmount, setReloadAmount] = useState(25);
  const [reloading, setReloading] = useState(false);
  const [designs, setDesigns] = useState([FALLBACK_DESIGN]);
  useEffect(() => { api.get("/gift-cards/designs").then((r) => setDesigns([...(r.data.custom || []), ...(r.data.defaults || [])])).catch(() => {}); }, []);
  const design = data ? (designs.find((d) => d.key === (data.card.theme || "classic")) || FALLBACK_DESIGN) : FALLBACK_DESIGN;
  const inp = "w-full rounded-lg bg-black/40 border border-white/10 focus:border-[#df1721] focus:ring-0 outline-none px-4 py-3 text-sm";

  const lookup = async () => {
    if (!code || !pin) { toast.error("Enter both code and PIN"); return; }
    setBusy(true); setData(null);
    try {
      const r = await api.post("/gift-cards/lookup", { code, pin });
      setData(r.data);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  const reload = async () => {
    setReloading(true);
    try {
      const r = await api.post("/gift-cards/reload/checkout", {
        code, pin, amount: Number(reloadAmount),
        origin_url: window.location.origin,
      });
      window.location.href = r.data.url;
    } catch (e) { toast.error(errMsg(e)); setReloading(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 md:px-10 py-14" data-testid="gift-card-check">
      <p className="inline-flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-widest bg-[#df1721] text-black px-3 py-1.5 rounded-full mb-4">
        <Wallet size={13} /> Balance & Reload
      </p>
      <h1 className="font-display font-black text-3xl md:text-4xl">Check your gift card.</h1>
      <p className="text-zinc-400 mt-2">Enter the code and PIN from your gift card email to see the balance, history and reload.</p>

      <div className="mt-8 rounded-2xl border border-white/10 bg-white/[0.03] p-6 grid sm:grid-cols-2 gap-3">
        <label className="sm:col-span-2"><span className="text-xs text-zinc-400">Gift card code</span>
          <input className={inp + " mt-1 font-mono tracking-widest"} value={code} placeholder="GCV-XXXX-XXXX-XXXX"
            onChange={(e) => setCode(e.target.value.toUpperCase())} data-testid="gc-check-code" /></label>
        <label><span className="text-xs text-zinc-400">PIN</span>
          <input className={inp + " mt-1 font-mono tracking-widest"} value={pin} placeholder="••••"
            onChange={(e) => setPin(e.target.value)} data-testid="gc-check-pin" /></label>
        <button onClick={lookup} disabled={busy}
          className="bg-[#df1721] hover:bg-[#c0141d] text-black font-bold py-3 rounded-lg text-sm mt-auto disabled:opacity-40 flex items-center justify-center gap-2"
          data-testid="gc-check-submit">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />} Check balance
        </button>
      </div>

      {data && (
        <div className="mt-8 space-y-6" data-testid="gc-check-result">
          <div className="grid md:grid-cols-2 gap-6 items-center">
            <CardMock amount={data.card.current_balance} code={data.card.code}
              scope={data.card.scope} sellerName={data.card.seller_name} design={design} />
            <div>
              <p className="overline mb-1">Current balance</p>
              <p className="font-display font-black text-5xl">{fmtM(data.card.current_balance)}</p>
              <p className="text-sm text-zinc-400 mt-2">Initial: {fmtM(data.card.initial_amount)} · Redeemed: {fmtM(data.card.redeemed_amount)} · Reloaded: {fmtM(data.card.reloaded_amount)}</p>
              <p className="text-sm mt-3">Status: <span className="font-bold text-[#df1721]">{data.card.status}</span> · Scope: <span className="font-bold">{data.card.scope}{data.card.scope === "merchant" && data.card.seller_name ? ` (${data.card.seller_name})` : ""}</span></p>
              {data.card.allow_reload && data.card.status !== "cancelled" && (
                <div className="mt-5 flex flex-wrap items-center gap-2">
                  <select className={inp + " max-w-[140px]"} value={reloadAmount}
                    onChange={(e) => setReloadAmount(e.target.value)} data-testid="gc-reload-amount">
                    {[10, 25, 50, 100, 250].map((a) => <option key={a} value={a}>Reload ${a}</option>)}
                  </select>
                  <button onClick={reload} disabled={reloading}
                    className="bg-[#df1721] hover:bg-[#c0141d] text-black font-bold px-5 py-3 rounded-lg text-sm disabled:opacity-40 flex items-center gap-2"
                    data-testid="gc-reload-submit">
                    {reloading ? <Loader2 size={15} className="animate-spin" /> : <RefreshCcw size={15} />} Reload
                  </button>
                </div>
              )}
            </div>
          </div>
          <div>
            <p className="overline mb-2">Recent transactions</p>
            <div className="rounded-2xl border border-white/10 divide-y divide-white/5 bg-white/[0.02]" data-testid="gc-transactions">
              {(data.transactions || []).map((t) => (
                <div key={t.id} className="flex items-center justify-between px-4 py-3 text-sm">
                  <div>
                    <p className="font-medium capitalize">{t.kind}</p>
                    <p className="text-xs text-zinc-500">{fmtD(t.created_at)}{t.note ? ` · ${t.note}` : ""}</p>
                  </div>
                  <div className="text-right">
                    <p className={`font-bold ${t.amount > 0 ? "text-emerald-400" : "text-[#df1721]"}`}>{t.amount > 0 ? "+" : ""}{fmtM(t.amount)}</p>
                    <p className="text-xs text-zinc-500">Bal {fmtM(t.balance_after)}</p>
                  </div>
                </div>
              ))}
              {(data.transactions || []).length === 0 && <p className="text-sm text-zinc-500 py-6 text-center">No transactions yet</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ============================ /gift-cards/purchase-success ============================ */
export function GiftCardPurchaseSuccess() {
  useSeo("Gift card sent | Gulf Coast VIP", "Your Gulf Coast VIP gift card purchase is complete.");
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const [data, setData] = useState({ status: "pending" });
  const [designs, setDesigns] = useState([FALLBACK_DESIGN]);
  useEffect(() => { api.get("/gift-cards/designs").then((r) => setDesigns([...(r.data.custom || []), ...(r.data.defaults || [])])).catch(() => {}); }, []);
  useEffect(() => {
    let stop = false;
    let attempts = 0;
    const tick = async () => {
      if (stop) return;
      try {
        const r = await api.get(`/gift-cards/purchase/status?session_id=${sid}`);
        setData(r.data);
        if (r.data.status === "paid" || attempts >= 20) return;
      } catch { /* keep polling */ }
      attempts += 1;
      setTimeout(tick, 1500);
    };
    if (sid) tick();
    return () => { stop = true; };
  }, [sid]);
  const design = designs.find((d) => d.key === (data.theme || "classic")) || FALLBACK_DESIGN;
  return (
    <div className="max-w-3xl mx-auto px-6 md:px-10 py-20" data-testid="gc-purchase-success">
      <GiftCardStyles />
      {data.status === "paid" ? (
        <div className="text-center">
          <CheckCircle2 size={48} className="mx-auto text-emerald-400" />
          <h1 className="font-display font-black text-3xl md:text-4xl mt-4">Gift card on its way! 🎉</h1>
          <p className="text-zinc-300 mt-3">
            {data.scheduled
              ? <>Payment confirmed. The card will be emailed to <strong>{data.recipient_email}</strong> on <strong>{fmtD(data.delivery_at)}</strong>.</>
              : <>The card was emailed to <strong>{data.recipient_email}</strong> with the code, PIN and a QR code.</>}
          </p>
          <div className="mt-8 mx-auto max-w-sm">
            <CardMock amount={data.amount} code={data.card_code} scope={data.scope}
              sellerName={data.seller_name} recipient={data.recipient_name} design={design} />
          </div>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link to="/gift-cards" className="bg-[#df1721] hover:bg-[#c0141d] text-white font-bold px-6 py-3 rounded-full text-sm inline-flex items-center gap-2" data-testid="gc-buy-another">
              <Gift size={15} /> Send another card
            </Link>
            <Link to={`/gift-cards/check?code=${data.card_code}`} className="border border-white/20 hover:border-[#df1721] text-white font-medium px-6 py-3 rounded-full text-sm inline-flex items-center gap-2">
              <Wallet size={15} /> Check balance
            </Link>
          </div>
        </div>
      ) : (
        <div className="text-center">
          <Loader2 size={40} className="mx-auto text-[#df1721] animate-spin" />
          <h1 className="font-display font-black text-2xl mt-4">Finishing your purchase…</h1>
          <p className="text-zinc-400 mt-2">Confirming with the payment processor and minting the card. This usually takes a couple of seconds.</p>
        </div>
      )}
    </div>
  );
}
export function GiftCardReloadSuccess() {
  useSeo("Reload confirmed | Gulf Coast VIP", "Your gift card top-up is complete. The balance is available immediately.");
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const [status, setStatus] = useState({ status: "pending" });
  useEffect(() => {
    let stop = false;
    let attempts = 0;
    const tick = async () => {
      if (stop) return;
      try {
        const r = await api.get(`/gift-cards/reload/status?session_id=${sid}`);
        setStatus(r.data);
        if (r.data.status === "paid" || attempts >= 20) return;
      } catch { /* ignore */ }
      attempts += 1;
      setTimeout(tick, 1500);
    };
    if (sid) tick();
    return () => { stop = true; };
  }, [sid]);
  return (
    <div className="max-w-2xl mx-auto px-6 md:px-10 py-20 text-center" data-testid="gc-reload-success">
      {status.status === "paid" ? (
        <>
          <CheckCircle2 size={48} className="mx-auto text-emerald-400" />
          <h1 className="font-display font-black text-3xl mt-4">Reload complete</h1>
          <p className="text-zinc-300 mt-2">Added <strong>{fmtM(status.amount)}</strong> to your card. The new balance is available right away.</p>
          <Link to={`/gift-cards/check?code=${status.code}`} className="mt-6 inline-flex items-center gap-2 bg-[#df1721] hover:bg-[#c0141d] text-black font-bold px-5 py-2.5 rounded-full text-sm">
            View updated balance <ChevronRight size={15} />
          </Link>
        </>
      ) : (
        <>
          <Loader2 size={40} className="mx-auto text-[#df1721] animate-spin" />
          <h1 className="font-display font-black text-2xl mt-4">Finishing your reload…</h1>
          <p className="text-zinc-400 mt-2">Confirming with the payment processor. This usually takes a couple of seconds.</p>
        </>
      )}
    </div>
  );
}
